/**
 * What to do once the running bundle is known to be stale.
 *
 *                      unsaved work?
 *                   yes ──────┴────── no
 *                    │                 │
 *   trigger:  any ──► prompt    navigation ──► reload
 *                               poll       ──► prompt
 *
 * A reload destroys unsaved grid rows, debounced drafts, in-flight saves and
 * an AI-chat upload still going up, so `work in progress` always wins. With
 * nothing in progress, only the navigation boundary is a safe moment: the
 * viewport is being replaced anyway, and the route-scoped editing store has
 * just been torn down. A poll fires at an arbitrary instant — reloading there
 * yanks the page out from under someone merely reading it — so it asks.
 */

/** Why a staleness decision is being made. */
export type StaleBundleTrigger = 'poll' | 'navigation';

export type StaleBundleAction =
  /** Running the current bundle, or staleness cannot be determined. */
  | 'ignore'
  /** Swap the tab onto the deployed bundle now. */
  | 'reload'
  /** Offer the swap, let the user pick the moment. */
  | 'prompt';

export type ReloadSafetyInput = {
  isStale: boolean;
  /** True while a grid row, debounced draft, in-flight save or chat upload is outstanding. */
  hasUnsavedWork: boolean;
  trigger: StaleBundleTrigger;
};

export function decideStaleBundleAction({ isStale, hasUnsavedWork, trigger }: ReloadSafetyInput): StaleBundleAction {
  if (!isStale) return 'ignore';
  if (hasUnsavedWork) return 'prompt';

  return trigger === 'navigation' ? 'reload' : 'prompt';
}
